import {
  formatAud,
  formatPercentFromPct,
} from '@/pages/blueprint-lite/utils/formatters';

import type { PurchaseAssessmentResult } from '../../../assessment/types';
import s from '../styles/dashboard.module.css';

interface Props {
  result: PurchaseAssessmentResult;
}

const KeyMetrics = ({ result }: Props) => {
  const hasPurchase = result.input.purchase !== null;
  const stampPlusFees = result.loan.stampDuty.total + result.loan.fees;
  const cf = result.cashflow;
  const lvrTone =
    result.loan.lvr > 90 ? 'neg' : result.loan.lvr > 80 ? 'warn' : 'pos';
  const cfTone = cf && cf.afterTaxCashflow < 0 ? 'neg' : 'pos';

  return (
    <section className={s.metrics}>
      <div className={`${s.metric} ${s.primary}`}>
        <span className={s.metricLabel}>Borrowing capacity</span>
        <div className={s.metricValue}>{formatAud(result.borrowingLow)}</div>
        <div className={s.metricSub}>
          to <strong>{formatAud(result.borrowingHigh)}</strong>
          <br />
          {result.passCount} lender{result.passCount !== 1 ? 's' : ''} eligible
        </div>
      </div>

      <div className={s.metric}>
        <span className={s.metricLabel}>Loan-to-value</span>
        <div className={`${s.metricValue} ${s[lvrTone]}`}>
          {formatPercentFromPct(result.loan.lvr)}
        </div>
        <div className={s.metricSub}>
          Loan <strong>{formatAud(result.loan.loanAmount)}</strong>
          <br />
          Target{' '}
          <strong>
            {formatAud(result.input.purchase?.purchasePrice ?? 0)}
          </strong>
        </div>
      </div>

      <div className={s.metric}>
        <span className={s.metricLabel}>Stamp duty &amp; fees</span>
        <div className={`${s.metricValue} ${s.metricValueSmall}`}>
          {hasPurchase ? formatAud(stampPlusFees) : 'N/A'}
        </div>
        <div className={s.metricSub}>
          {hasPurchase ? (
            <>
              SD <strong>{formatAud(result.loan.stampDuty.total)}</strong>
              {result.loan.stampDuty.foreign > 0 && ' (incl. foreign)'}
              <br />
              Fees <strong>{formatAud(result.loan.fees)}</strong>
            </>
          ) : (
            'No purchase target on file'
          )}
        </div>
      </div>

      <div className={s.metric}>
        <span className={s.metricLabel}>Cashflow (after tax)</span>
        <div
          className={`${s.metricValue} ${s.metricValueSmall} ${cf ? s[cfTone] : ''}`}
        >
          {cf ? `${formatAud(cf.afterTaxCashflow)}/yr` : 'N/A'}
        </div>
        <div className={s.metricSub}>
          {cf
            ? cf.afterTaxCashflow < 0
              ? `Pre-tax loss ${formatAud(Math.abs(cf.preTaxCashflow))} · offset ${formatAud(cf.negGearingOffset)}`
              : 'Positive cashflow position'
            : 'Awaiting inputs'}
        </div>
      </div>
    </section>
  );
};

export default KeyMetrics;
